// components/Notifications.jsx
import React, { useContext, useEffect, useState, useCallback } from "react";
import { DContext } from "../context/Datacontext";
import { fetchNotification } from "../services/fetchNotification";
import { Pagination } from "../utils/Pagination";
import NotificationModal from "../components/NotificationModal";


const buildQuery = (baseUrl, params = {}) => {
    const qs = new URLSearchParams();
    Object.keys(params).forEach((k) => {
        const v = params[k];
        if (v === undefined || v === null) return;

        if (typeof v === "object") {
            qs.append(k, JSON.stringify(v));
        } else {
            qs.append(k, String(v));
        }
    });
    return `${baseUrl}?${qs.toString()}`;
};

export const Notifications = () => {
    const { BeURL } = useContext(DContext);

    const [notifications, setNotifications] = useState([]);
    const [filters, setFilters] = useState({});
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [totalPages, setTotalPages] = useState(1);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const [viewNotificationId, setViewNotificationId] = useState(null);

    const selectedNotification = notifications.find(
        (item) => item._id === viewNotificationId
    );

    const load = useCallback(async () => {
        setLoading(true);
        setError("");

        try {
            const url = buildQuery(`${BeURL}/notifications`, { page, limit, filters });
            const result = await fetchNotification(url);

            if (result && result.success) {
                const notif = result.notification || [];
                setNotifications(notif);
                setTotal(result.total ?? 0);
                setTotalPages(result.totalPages ?? 1);
            } else {
                setError(result?.message || "Failed to fetch notifications");
                setNotifications([]);
            }
        } catch (err) {
            setError(err.message || "Something went wrong");
            setNotifications([]);
        } finally {
            setLoading(false);
        }
    }, [BeURL, page, limit, filters]);

    const handleMarkAsRead = async (notificationId) => {
        setViewNotificationId(notificationId);
        try {
            const response = await fetch(
                `${BeURL}/notification/mark-as-read?notificationId=${notificationId}`,
                { method: "GET", credentials: "include" }
            );

            if (!response.ok) {
                const data = await response.json();
                alert(data.message);
            } else {
                setNotifications((prev) =>
                    prev.map((notif) =>
                        notif._id === notificationId ? { ...notif, isRead: true } : notif
                    )
                );
            }
        } catch (err) {
            setError(err.message || "Something went wrong");
        }
    };

    useEffect(() => {
        if (!BeURL) return;
        load();
    }, [BeURL, load]);

    const onFilterChange = (newFilters) => {
        setFilters(newFilters);
        setPage(1);
    };

    return (
        <div className="p-5">
            <h1 className="text-xl font-semibold mb-4">Notifications</h1>

            {loading && <p>Loading...</p>}
            {error && <p className="text-red-500">{error}</p>}

            {!loading && !error && notifications.length === 0 && <p>No notifications found</p>}

            {!loading && notifications.length > 0 && (
                <>
                    <div className="w-full overflow-x-auto rounded-xl shadow-md border border-gray-200 mt-4">
                        <table className="w-full text-sm text-left bg-white whitespace-nowrap">
                            <thead className="bg-primary-100 text-primary-600 text-xs uppercase">
                                <tr>
                                    <th className="p-3 border text-center">Sl.no</th>
                                    <th className="p-3 border text-center">Notification ID</th>
                                    <th className="p-3 border text-center">Type</th>
                                    <th className="p-3 border text-center">Created At</th>
                                    <th className="p-3 border text-center">Status</th>
                                </tr>
                            </thead>

                            <tbody className="divide-y divide-gray-100">
                                {notifications.map((item, index) => (
                                    <tr
                                        key={item._id}
                                        className={`text-sm ${item?.isRead ? "text-gray-400" : "font-semibold"}`}
                                    >
                                        <td className="p-3 border text-center">{(page - 1) * limit + index + 1}</td>
                                        <td className="p-3 border text-center">{item.notificationId || "-"}</td>
                                        <td className="p-3 border whitespace-nowrap">{item.title || "-"}</td>
                                        <td className="p-3 border text-center">
                                            {new Date(item.createdAt).toLocaleString()}
                                        </td>
                                        <td className="p-3 border text-center space-x-2">
                                            <button
                                                onClick={() => handleMarkAsRead(item._id)}
                                                className="ml-2 text-xs bg-primary-600 px-2 py-1 rounded text-white"
                                            >
                                                View
                                            </button>

                                            <span
                                                className={`${item.isRead ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"
                                                    } px-2 py-1 text-xs rounded`}
                                            >
                                                {item.isRead ? "Read" : "Unread"}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <Pagination
                        page={page}
                        totalPages={totalPages}
                        limit={limit}
                        totalUsers={total}
                        setPage={setPage}
                        setLimit={setLimit}
                    />
                </>
            )}

            {/* Modern Popup Modal */}
            <NotificationModal
                open={!!viewNotificationId}
                onClose={() => setViewNotificationId(null)}
                notification={selectedNotification}
            />
        </div>
    );
};